import React, { useState } from "react";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

// Reusable newsletter signup. `variant`:
//   - "light" — for light backgrounds (used in footer-like inverted sections)
//   - "dark"  — for dark backgrounds
export default function NewsletterSignup({ variant = "dark", source = "newsletter", className = "" }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/subscribe", { email, name: name || null, source, website });
      setDone(true);
      toast.success("You're subscribed! Welcome to Titan.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className={`flex items-center gap-3 ${className}`} data-testid="newsletter-done">
        <CheckCircle2 className="text-[#e63946] flex-shrink-0" size={24} />
        <div>
          <div className="font-display text-lg font-semibold text-[#0d2040]">Welcome aboard!</div>
          <p className="text-sm text-gray-600 mt-0.5">Watch your inbox for exclusive Ghana car-rental offers.</p>
        </div>
      </div>
    );
  }

  const isDark = variant === "dark";
  return (
    <form onSubmit={submit} className={className} data-testid="newsletter-form">
      {/* Honeypot field */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />
      <div className="flex items-center gap-2 mb-2">
        <Mail size={14} className={isDark ? "text-[#ff8a93]" : "text-[#e63946]"} />
        <span className={`eyebrow ${isDark ? "text-[#ff8a93]" : ""}`}>Stay in the loop</span>
      </div>
      <h3 className={`font-display text-2xl font-bold ${isDark ? "text-white" : "text-[#0d2040]"}`}>
        Get 10% off your first rental
      </h3>
      <p className={`text-sm mt-1.5 ${isDark ? "text-white/70" : "text-gray-600"}`}>
        Subscribe for exclusive deals on long-term rentals, new arrivals & seasonal offers.
      </p>
      <div className="mt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-2">
        <input
          type="text"
          placeholder="Your name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={`px-3 py-2.5 text-sm border ${isDark ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : "bg-white border-gray-300 text-[#0d2040]"} focus:outline-none focus:border-[#e63946]`}
          data-testid="newsletter-name"
        />
        <input
          type="email"
          placeholder="you@email.com"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`px-3 py-2.5 text-sm border ${isDark ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : "bg-white border-gray-300 text-[#0d2040]"} focus:outline-none focus:border-[#e63946]`}
          data-testid="newsletter-email"
        />
        <button type="submit" disabled={submitting} className="btn-primary" data-testid="newsletter-submit">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : "Subscribe"}
        </button>
      </div>
      <p className={`text-[0.7rem] mt-2 ${isDark ? "text-white/40" : "text-gray-400"}`}>
        No spam. Unsubscribe anytime. We respect your privacy.
      </p>
    </form>
  );
}
